/**
 * Main Application Root Component
 * 
 * Serves as the entry point for the ContactGenie application
 * Sets up the routing infrastructure and authentication wrapper
 * 
 * Architecture:
 * - HashRouter: Enables client-side routing using URL hash fragments
 *   Used instead of BrowserRouter to work with file-based deployment
 *   URLs appear as /#/path/to/page instead of /path/to/page
 * - AuthWrapper: Manages authentication state, protected routes, and user session
 * 
 * The component structure:
 * <App>
 *   <HashRouter>
 *     <AuthWrapper> (handles auth, token management, and route protection)
 *       <Routes>
 *         <Route path="/" element={<Home />} />
 *         ... (all app routes)
 *       </Routes>
 *     </AuthWrapper>
 *   </HashRouter>
 * </App>
 * 
 * Features:
 * - Client-side routing with hash-based navigation
 * - Automatic authentication state management
 * - Protected route access control
 * - Token refresh and session management
 * 
 * Dependencies:
 * - React: UI library
 * - react-router-dom: Routing library (HashRouter for client-side routing)
 * - ./auth/AuthWrapper: Authentication and route protection component
 * 
 * @component
 * @returns {JSX.Element} Root application component with routing and auth wrapper
 * 
 * @example
 * // In index.js, App is rendered as the root component
 * import App from './App';
 * const root = ReactDOM.createRoot(document.getElementById('root'));
 * root.render(<App />);
 */

import React from "react";
import { HashRouter } from 'react-router-dom';  // Change to HashRouter for file-based deployment
import { AuthWrapper } from './auth/AuthWrapper';

/**
 * App Component
 * Root component of the ContactGenie application
 * Wraps the application with routing and authentication providers
 * 
 * @returns {JSX.Element} Application container with routing and auth
 */
function App() {
  return (
    <div className="App">
      {/* HashRouter enables client-side routing using URL hash fragments (/#/path) */}
      {/* This works better for file-based deployments compared to BrowserRouter */}
      <HashRouter>
        {/* AuthWrapper handles user authentication, session management, and route protection */}
        <AuthWrapper />
      </HashRouter>      
    </div>
  );
}

export default App;
